import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import { LoginWallProvider } from "@/components/LoginWall";
import RequireAuth from "@/components/RequireAuth";
import AuthCallback from "@/components/AuthCallback";
import { SupabaseSessionProvider } from "@/lib/SupabaseSessionContext";
import { PresenceProvider } from "@/lib/PresenceContext";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import TeammatesPage from "@/pages/TeammatesPage";
import WhatToPlayPage from "@/pages/WhatToPlayPage";
import FpsFixPage from "@/pages/FpsFixPage";
import TrendsPage from "@/pages/TrendsPage";
import MetaBuildsPage from "@/pages/MetaBuildsPage";
import AICoachPage from "@/pages/AICoachPage";
import ProfilePage from "@/pages/ProfilePage";
import PremiumPage from "@/pages/PremiumPage";
import AdminPage from "@/pages/AdminPage";
import ZombiesHubPage from "@/pages/ZombiesHubPage";
import ZombieMapPage from "@/pages/ZombieMapPage";
import GameHubPage from "@/pages/GameHubPage";
import HubIndexPage from "@/pages/HubIndexPage";
import PlayersPage from "@/pages/PlayersPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import CheckoutCancelPage from "@/pages/CheckoutCancelPage";
import ChatPage from "@/pages/ChatPage";
import NotificationsCenterPage from "@/pages/NotificationsCenterPage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import FriendsPage from "@/pages/FriendsPage";
import SquadsPage from "@/pages/SquadsPage";

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: Detect OAuth callback synchronously during render.
  // useEffect runs AFTER first render — too late & causes race with RequireAuth.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/hub" element={<HubIndexPage />} />
      <Route path="/hub/:slug" element={<GameHubPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/pricing" element={<PremiumPage />} />
      <Route path="/success" element={<CheckoutSuccessPage />} />
      <Route path="/cancel" element={<CheckoutCancelPage />} />

      {/* Guest-previewable feature pages */}
      <Route path="/app/trends" element={<TrendsPage />} />
      <Route path="/app/teammates" element={<TeammatesPage />} />
      <Route path="/app/play" element={<WhatToPlayPage />} />
      <Route path="/app/premium" element={<PremiumPage />} />
      <Route path="/app/zombies" element={<ZombiesHubPage />} />
      <Route path="/app/zombies/map/:mapId" element={<ZombieMapPage />} />
      <Route path="/app/hub/:slug" element={<GameHubPage />} />
      <Route path="/u/:username" element={<PublicProfilePage />} />

      {/* Gated */}
      <Route
        path="/app/chat"
        element={<RequireAuth><ChatPage /></RequireAuth>}
      />
      <Route
        path="/app/chat/:conversationId"
        element={<RequireAuth><ChatPage /></RequireAuth>}
      />
      <Route
        path="/app/notifications"
        element={<RequireAuth><NotificationsCenterPage /></RequireAuth>}
      />
      <Route
        path="/app/friends"
        element={<RequireAuth><FriendsPage /></RequireAuth>}
      />
      <Route
        path="/app/squads"
        element={<RequireAuth><SquadsPage /></RequireAuth>}
      />
      <Route
        path="/app/fps-fix"
        element={<RequireAuth><FpsFixPage /></RequireAuth>}
      />
      <Route
        path="/app/builds"
        element={<RequireAuth><MetaBuildsPage /></RequireAuth>}
      />
      <Route
        path="/onboarding"
        element={<RequireAuth requireOnboarded={false}><OnboardingPage /></RequireAuth>}
      />
      <Route
        path="/app"
        element={<RequireAuth><DashboardPage /></RequireAuth>}
      />
      <Route
        path="/app/coach"
        element={<RequireAuth><AICoachPage /></RequireAuth>}
      />
      <Route
        path="/app/profile"
        element={<RequireAuth><ProfilePage /></RequireAuth>}
      />
      <Route
        path="/app/players"
        element={<RequireAuth><PlayersPage /></RequireAuth>}
      />
      <Route
        path="/app/admin"
        element={<RequireAuth><AdminPage /></RequireAuth>}
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <SupabaseSessionProvider>
              <PresenceProvider>
                <LoginWallProvider>
                  <AppRoutes />
                  <Toaster
                    theme="dark"
                    position="top-center"
                    toastOptions={{
                      style: {
                        background: "#121214",
                        color: "#fff",
                        border: "1px solid rgba(0,229,255,0.25)",
                      },
                    }}
                  />
                </LoginWallProvider>
              </PresenceProvider>
            </SupabaseSessionProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
